import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { saveAs } from "file-saver";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { api } from "../../service/api.service";

const DAILY_API_URL = `${api.defaults.baseURL}admin/daily-darshan`;
const SIX_DHAM_API_URL = `${api.defaults.baseURL}admin/daily-darshan/dham`;

const templeNames = [
  "Shri Swaminarayan Mandir, Vedroad-surat",
  "Shri Swaminarayan Mandir, Ahmedabad",
];

const getTempleLabel = (temple) => {
  if (temple?.ShortName) return temple.ShortName;
  if (temple?.mandir) return temple.mandir.replace(/-Temple$/i, "");
  return "Temple";
};

const normalizeTemple = (temple) => ({
  ...temple,
  label: getTempleLabel(temple),
});

export default function Dailydarshan() {
  const [activeTemple, setActiveTemple] = useState(templeNames[0]);
  const [dailyDarshan, setDailyDarshan] = useState({
    fileUrls: [],
    fileHdUrls: [],
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [temples, setTemples] = useState([]);
  const [activeDhamIndex, setActiveDhamIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [previewImage, setPreviewImage] = useState(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    fetch(DAILY_API_URL)
      .then((res) => res.json())
      .then((data) => {
        setDailyDarshan({
          fileUrls: data?.data?.fileUrls || [],
          fileHdUrls: data?.data?.fileHdUrls || [],
        });
        setCurrentIndex(0);
      })
      .catch((err) => console.error("Error fetching daily darshan:", err));
  }, []);

  useEffect(() => {
    fetch(SIX_DHAM_API_URL)
      .then((response) => response.json())
      .then((payload) => {
        const items = Array.isArray(payload?.data)
          ? payload.data.map(normalizeTemple)
          : [];
        setTemples(items);
        setActiveDhamIndex(0);
        setActiveImageIndex(0);
      })
      .catch((error) => {
        console.error("Error fetching six dham darshan:", error);
      });
  }, []);

  const totalImages = dailyDarshan.fileUrls?.length || 0;
  const visibleCount = 4;

  const images = totalImages
    ? dailyDarshan.fileUrls
    : [
        "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=900&q=80",
      ];

  const handlePrevImages = () => {
    if (totalImages <= 1) return;
    setCurrentIndex((prev) => (prev - 1 + totalImages) % totalImages);
  };

  const handleNextImages = () => {
    if (totalImages <= 1) return;
    setCurrentIndex((prev) => (prev + 1) % totalImages);
  };

  const handleDownload = (index) => {
    if (!totalImages) return;

    const actualIndex = (currentIndex + index) % totalImages;
    const selectedUrl =
      dailyDarshan.fileHdUrls?.[actualIndex] || dailyDarshan.fileUrls?.[actualIndex];

    if (selectedUrl) saveAs(selectedUrl);
  };

  const vedroadTemple = useMemo(
    () => ({
      label: "Vedroad-surat Darshan",
      ShortName: "surat",
      images: Array.isArray(dailyDarshan.fileUrls) && dailyDarshan.fileUrls.length
        ? dailyDarshan.fileUrls
        : [
            "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=900&q=80",
          ],
    }),
    [dailyDarshan.fileUrls]
  );

  const activeTempleDham = useMemo(() => {
    if (activeDhamIndex === 0 && dailyDarshan.fileUrls?.length) {
      return vedroadTemple;
    }

    return temples[activeDhamIndex - 1] || null;
  }, [temples, activeDhamIndex, dailyDarshan.fileUrls, vedroadTemple]);

  const currentGalleryImages = useMemo(() => {
    if (Array.isArray(activeTempleDham?.images) && activeTempleDham.images.length) {
      return activeTempleDham.images;
    }

    return [
      "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=900&q=80",
    ];
  }, [activeTempleDham]);

  const sliderSettings = useMemo(
    () => ({
      dots: true,
      arrows: false,
      infinite: true,
      autoplay: true,
      autoplaySpeed: 2800,
      speed: 700,
      slidesToShow: Math.min(4, currentGalleryImages.length || 1),
      slidesToScroll: 1,
      centerMode: false,
      cssEase: "cubic-bezier(0.22, 1, 0.36, 1)",
      pauseOnHover: true,
      swipeToSlide: true,
      responsive: [
        { breakpoint: 1280, settings: { slidesToShow: 3 } },
        { breakpoint: 768, settings: { slidesToShow: 2 } },
        { breakpoint: 480, settings: { slidesToShow: 1 } },
      ],
    }),
    [currentGalleryImages.length]
  );

  const moveImage = (direction) => {
    if (!sliderRef.current) return;

    if (direction < 0) sliderRef.current.slickPrev();
    else sliderRef.current.slickNext();
  };

  const moveTemple = (direction) => {
    const total = temples.length + 1;
    if (!total) return;

    const nextIndex = (activeDhamIndex + direction + total) % total;
    setActiveDhamIndex(nextIndex);
    setActiveImageIndex(0);
  };

  const handlePreviewImage = (url) => {
    if (!url) return;
    setPreviewImage(url);
  };

  const handleDownloadImage = (image, index) => {
    let url = image;

    if (activeDhamIndex === 0) {
      url = dailyDarshan.fileHdUrls?.[index] || dailyDarshan.fileUrls?.[index] || image;
    }

    if (!url) return;
    saveAs(url);
  };

  return (
    <>
               
      <div className="overflow-hidden">
        <img src="/assets/shapeline.png" alt="shape-line-top" className="block h-auto w-full rotate-180 opacity-90" />
      </div>

      <section className="bg-[#FFF5F3] py-16 lg:py-20 px-4 md:px-8 ">
        
        <div className="mx-auto max-w-[1550px]">
          <h1
            className="text-center text-primary text-[clamp(2.5rem,4vw,5rem)] font-bold leading-none"
            style={{ fontFamily: "Philosopher, sans-serif" }}
          >
            Daily Darshan
          </h1>

          <p className="mx-auto mt-5 max-w-4xl text-center text-darkgray text-base md:text-[1.38rem] leading-relaxed">
            Receive divine blessings every day with darshan from Shri Swaminarayan Mandir, Ved Road–Surat, and the six holy Dham of Bhagwan Swaminarayan.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4 md:gap-6">
            <button
              key="vedroad-surat-tab"
              type="button"
              onClick={() => {
                setActiveDhamIndex(0);
                setActiveImageIndex(0);
              }}
              className={[
                "relative flex items-center gap-3 rounded-full border px-4 py-2.5 text-base font-medium transition-all duration-200 md:px-5",
                activeDhamIndex === 0
                  ? "border-primary bg-white text-black shadow-[0_4px_14px_rgba(216,2,13,0.12)]"
                  : "border-[#f2d3ce] bg-white text-[#1e1e1e] hover:border-primary",
              ].join(" ")}
            >
              <img
                src={
                  vedroadTemple.images?.[0] ||
                  "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=500&q=80"
                }
                alt="Vedroad-surat Darshan"
                className="h-10 w-10 rounded-full object-cover border border-white shadow-sm"
              />
              <span>Vedroad-surat</span>
            </button>

            {temples.map((temple, index) => {
              const isActive = activeDhamIndex === index + 1;

              return (
                <button
                  key={`${temple.label}-${index}`}
                  type="button"
                  onClick={() => {
                    setActiveDhamIndex(index + 1);
                    setActiveImageIndex(0);
                  }}
                  className={[
                    "relative flex items-center gap-3 rounded-full border px-4 py-2.5 text-base font-medium transition-all duration-200 md:px-5",
                    isActive
                      ? "border-primary bg-white text-black shadow-[0_4px_14px_rgba(216,2,13,0.12)]"
                      : "border-[#f2d3ce] bg-white text-[#1e1e1e] hover:border-primary",
                  ].join(" ")}
                >
                  <img
                    src={
                      temple.images?.[0] ||
                      "https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=500&q=80"
                    }
                    alt={temple.label}
                    className="h-10 w-10 rounded-full object-cover border border-white shadow-sm"
                  />
                  <span>{temple.label}</span>
                </button>
              );
            })}

          </div>

          {activeTempleDham && (
            <div className="mt-12 flex items-center justify-center gap-3 md:gap-5">
              <button
                type="button"
                aria-label="Previous image"
                onClick={() => moveImage(-1)}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFE0DC] text-3xl font-light text-primary shadow-sm transition hover:bg-[#ffd9d3]"
              >
                ‹
              </button>

              <div className="w-full max-w-[1380px]">
                <Slider ref={sliderRef} {...sliderSettings}>
                  {currentGalleryImages.map((image, index) => (
                    <div key={`${activeTempleDham.label}-${index}`} className="px-2">
                      <div className="group relative cursor-pointer overflow-hidden rounded-[24px] border-4 border-transparent bg-white shadow-[0_10px_20px_rgba(51,25,7,0.08)] transition-all duration-300 hover:border-[#e9c6ae] hover:shadow-[0_12px_30px_rgba(217,96,47,0.21)]">
                        <div className="relative h-[360px] md:h-[440px] xl:h-[500px]">
                          <img
                            src={image}
                            alt={`${activeTempleDham.label} darshan ${index + 1}`}
                            className="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-105"
                          />

                          <div className="absolute inset-0 flex items-center justify-center gap-3 bg-[#000000]/40 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handlePreviewImage(image);
                              }}
                              className="rounded-full bg-white/90 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[#1f1f1f] shadow-md transition hover:bg-white"
                            >
                              View
                            </button>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDownloadImage(image, index);
                              }}
                              className="rounded-full bg-primary px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-md transition hover:bg-hoverprimary"
                            >
                              Download
                            </button>
                          </div>

                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#000000]/60 via-[#000000]/10 to-transparent p-3 md:p-4">
                            <div className="flex items-center justify-between gap-3 text-white">
                              <div>
                                <div className="text-sm font-medium md:text-base">
                                  {activeTempleDham.label}
                                </div>
                                <div className="text-[10px] md:text-xs text-white/80">
                                  {activeTempleDham.fdate || activeTempleDham.date || "Daily Darshan"}
                                </div>
                              </div>
                              <div className="rounded-full border border-white/60 bg-white/10 px-2 py-1 text-[9px] md:text-[10px] uppercase tracking-wide">
                                {index + 1}/{currentGalleryImages.length}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </Slider>
              </div>

              <button
                type="button"
                aria-label="Next image"
                onClick={() => moveImage(1)}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-[#FFE0DC] text-3xl font-light text-primary shadow-sm transition hover:bg-[#ffd9d3]"
              >
                ›
              </button>
            </div>
          )}
        </div>
      <div className="mt-8 flex justify-center">
            <Link
              to="/daily-darshan"
              className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 text-base font-medium uppercase text-white shadow-md transition hover:bg-hoverprimary"
            >
              View More
            </Link>
          </div>
      </section>

      <div className="overflow-hidden">
        <img src="/assets/shapeline.png" alt="shape-line-bottom" className="block h-auto w-full opacity-90" />
      </div>

      {previewImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-[22px] bg-white shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              onClick={() => setPreviewImage(null)}
              className="absolute right-3 top-3 z-10 rounded-full bg-white/90 px-3 py-1 text-sm font-bold text-[#1b1b1b] shadow-md"
            >
              ✕
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-h-[90vh] max-w-[90vw] object-contain"
            />
          </div>
        </div>
      )}
    </>
  );
}
